import React from "react";
import { motion } from "framer-motion";
import {
    TrendingDown,
    Activity,
    Clock,
    BarChart2,
} from "lucide-react";

const TheRealityGap = () => {

    // Data for the "Old Way" cards
    const outdatedMethods = [
        {
            id: 1,
            title: "RSI & Indicators",
            desc: "Standard oscillators that fail to account for institutional liquidity flows.",
            icon: Activity
        },
        {
            id: 2,
            title: "EMA & Moving Averages",
            desc: "Lagging data that only tells you what happened, not what will happen.",
            icon: TrendingDown
        },
        {
            id: 3,
            title: "Standard Price Action",
            desc: "Retail patterns (Head & Shoulders, Flags) that algorithms actively hunt.",
            icon: BarChart2
        },
        {
            id: 4,
            title: "Lagging Strategies",
            desc: "Systems that collapse the moment market volatility spikes.",
            icon: Clock
        }
    ];

    return (
        <section className="w-full py-24 px-4 sm:px-10 lg:px-14 xl:px-20 font-poppins relative overflow-hidden">

            {/* Background Atmosphere: Subtle Red Glow for "Danger" context */}
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-danger/5 blur-[120px] rounded-full pointer-events-none" />

            <div className="relative z-10 max-w-7xl mx-auto">

                {/* --------------------------- */}
                {/* HEADER SECTION              */}
                {/* --------------------------- */}
                <div className="lg:text-center mb-10 space-y-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-danger/10 border border-danger/20 text-danger text-sm font-semibold tracking-wide uppercase"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-danger animate-pulse"></span>
                        <span className="text-xs font-bold tracking-widest text-danger uppercase">
                            The Reality Gap
                        </span>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        /* FIX 1: Typography Scaling
                           - lg:text-5xl (Prevent wrapping on iPad)
                           - xl:text-6xl (Full size on Desktop)
                        */
                        className="text-3xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold text-dark dark:text-light leading-[1.1]"
                    >
                        <span className="block leading-[1.2]">The Market Has Changed.</span>
                        <span className="block leading-[1.2] text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                                Traditional Indicators Haven’t.
                </span>
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="text-lg text-dark/60 dark:text-light/60 max-w-2xl mx-auto leading-relaxed"
                    >
                        Most traders are stuck in a cycle of lagging data and retail traps.
                        Using tools built for the markets of the past guarantees results from the past.
                    </motion.p>
                </div>

                {/* --------------------------- */}
                {/* THE PROBLEM GRID            */}
                {/* --------------------------- */}
                {/*
                    FIX 2: Grid Breakpoints
                    - md:grid-cols-2 (Tablets/iPad Portrait stay here)
                    - xl:grid-cols-4 (Only Switch to 4 cols on screens wider than 1280px)

                    Previously `lg:grid-cols-4` forced 4 tiny columns on iPad Portrait (1024px).
                */}
                <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mb-16">
                    {outdatedMethods.map((item, index) => (
                        <ProblemCard key={item.id} item={item} index={index} />
                    ))}
                </div>

                {/* --------------------------- */}
                {/* THE CONCLUSION (BRIDGE)     */}
                {/* --------------------------- */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                    className="
                        relative overflow-hidden rounded-2xl
                        bg-white dark:bg-bg-dark-2
                        shadow-xl shadow-dark/5 dark:shadow-none
                        border border-primary/20 p-8 md:p-12 text-center
                    "
                >
                    {/* Blue Glow Behind */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-primary/5 blur-3xl pointer-events-none" />

                    <div className="relative z-10 flex flex-col items-center gap-6">
                        <h3 className="text-2xl md:text-3xl font-semibold text-dark dark:text-light">
                            These tools were built for the <span className="text-dark/40 dark:text-light/40 line-through decoration-danger decoration-2">past</span>.
                        </h3>

                        <div className="flex flex-col items-center gap-2">
                            <span className="text-primary font-bold tracking-wider text-sm uppercase">The Fortune Signals Approach</span>
                            <p className="text-3xl md:text-4xl font-bold text-dark dark:text-light">
                                Our methods are built for <span className="text-primary">tomorrow.</span>
                            </p>
                        </div>
                    </div>
                </motion.div>

            </div>
        </section>
    );
};

export default TheRealityGap;


/* ------------------------------------------------------------ */
/*                    SUB-COMPONENT: PROBLEM CARD               */
/* ------------------------------------------------------------ */

function ProblemCard({ item, index }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="
                group relative p-6 rounded-xl
                bg-white dark:bg-bg-dark-2
                border border-dark/5 dark:border-light/5
                shadow-sm hover:shadow-md
                hover:border-danger/30 hover:bg-danger/5
                transition-all duration-300
                h-full flex flex-col justify-start
            "
        >
            <div className="flex justify-between items-start mb-4">
                <div className="p-3 rounded-lg bg-bg-light-2 dark:bg-bg-dark-1 text-dark/50 dark:text-light/50 group-hover:text-danger transition-colors">
                    <item.icon size={24} />
                </div>
            </div>

            <h4 className="text-lg font-bold text-dark dark:text-light mb-2 group-hover:text-danger dark:group-hover:text-white transition-colors">
                {item.title}
            </h4>

            <p className="text-sm text-dark/60 dark:text-light/50 leading-relaxed">
                {item.desc}
            </p>
        </motion.div>
    );
}